import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle, Languages } from "lucide-react";
import { getLocalizedItemName, getItem } from "@/lib/data";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { LOCALE_LABELS, defaultLocale } from "@/lib/i18n/config";
import { SuggestTranslationModal } from "@/components/i18n/SuggestTranslationModal";

/**
 * Renders an item's localized name. When the name falls back to English, the text gets a
 * dotted underline; clicking it opens a popover (item photo + explanation) with a CTA that
 * opens SuggestTranslationModal, so anyone can add the missing translation right there.
 *
 * The popover is rendered via a portal into document.body rather than as a plain absolutely-
 * positioned child: this name is almost always inside a catalog card, and every catalog card
 * (RecipeStyleCard) has `overflow-hidden` for its rounded corners/hover-lift effect — an
 * in-place absolute popover would technically open (state flips, node mounts) but render
 * invisible, clipped by that ancestor. Portaling escapes the clip; position is computed from
 * the trigger button's on-screen rect at open time.
 *
 * Portaling is also why the popover stops click and Escape from propagating: a portal keeps its
 * place in the React tree, so events raised inside it still bubble to the card-wide <Link> that
 * (visually) contains nothing of the sort.
 */
export function LocalizedItemName({ itemId, className = "" }: { itemId: string; className?: string }) {
  const { locale, t } = useLocale();
  const localizedName = getLocalizedItemName(itemId, locale);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverTitleId = useId();

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setPopoverOpen(false);
    }
    // ⚠️ focusin and keydown are on the document, not on the panel, and deliberately so.
    // The panel is portalled to document.body — outside #root — and it holds exactly one
    // focusable. Handlers bound to the panel leave two holes: a second Tab moves focus out of
    // the portal (which sits *after* #root, so it wraps to the top of the page) while the panel
    // stays open, `position: fixed` against a trigger now thousands of pixels away; and Escape
    // stops working the moment focus is anywhere else, because the listener went with it.
    // Listening on the document closes both, and matches the mousedown handler above.
    function onFocusIn(e: FocusEvent) {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setPopoverOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closePopoverAndRestoreFocus();
    }
    if (popoverOpen) {
      document.addEventListener("mousedown", onMouseDown);
      document.addEventListener("focusin", onFocusIn);
      document.addEventListener("keydown", onKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [popoverOpen]);

  // Move focus into the popover when it opens: it puts the CTA one Tab away instead of at the
  // end of the document, and it gives Escape somewhere to be heard. preventScroll because the
  // popover is positioned over the trigger, which is already on screen.
  useEffect(() => {
    if (popoverOpen) popoverRef.current?.focus({ preventScroll: true });
  }, [popoverOpen]);

  if (!localizedName.isFallback || locale === defaultLocale) {
    return <span className={className}>{localizedName.text}</span>;
  }

  const item = getItem(itemId);
  const itemImage = item?.imageStatus === "verified" ? (item.image ?? null) : null;
  const localeLabel = LOCALE_LABELS[locale];

  function openPopover() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setPopoverPos({ top: rect.bottom + 8, left: rect.left });
    setPopoverOpen(true);
  }

  /** Closes and hands focus back to the trigger, so keyboard focus never lands on <body>. */
  function closePopoverAndRestoreFocus() {
    setPopoverOpen(false);
    buttonRef.current?.focus({ preventScroll: true });
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={popoverOpen}
        onClick={(e) => {
          // This name often sits inside a card-wide <Link> (catalog cards route the whole
          // card to the detail page) — without stopping the click here, the browser's default
          // anchor navigation fires on the same click and unmounts the page before the
          // popover ever gets to render.
          e.preventDefault();
          e.stopPropagation();
          if (popoverOpen) setPopoverOpen(false);
          else openPopover();
        }}
        className={`cursor-help decoration-amber-500 decoration-dotted decoration-2 underline underline-offset-4 transition-colors hover:text-amber-700 ${className}`.trim()}
      >
        {localizedName.text}
      </button>
      {popoverOpen && popoverPos
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-labelledby={popoverTitleId}
              tabIndex={-1}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                if (e.key !== "Escape") return;
                e.stopPropagation();
                closePopoverAndRestoreFocus();
              }}
              style={{ top: popoverPos.top, left: popoverPos.left }}
              className="fixed z-[1000] w-72 overflow-hidden rounded-2xl border-2 border-white bg-white/95 text-left normal-case shadow-2xl shadow-slate-900/15 outline-none backdrop-blur-xl"
            >
              <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white text-amber-700 shadow-sm">
                  <Languages className="size-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p id={popoverTitleId} className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-700">
                    {t("itemFallback.badge")}
                  </p>
                  <p className="truncate text-sm font-black text-slate-900">{localeLabel}</p>
                </div>
              </div>
              <div className="space-y-3 px-4 py-3">
                {itemImage ? <img src={itemImage} alt="" className="mx-auto h-20 w-20 object-contain" /> : null}
                <p className="text-sm font-semibold leading-5 text-slate-700">{t("itemFallback.helpMessage")}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Closes the popover and returns focus to the trigger. ⚠️ This does NOT
                    // survive the modal: Radix restores focus through its own triggerRef, not
                    // through document.activeElement, and these modals render no <DialogTrigger>
                    // — so on close the ref is null and focus lands on <body>. Pre-existing and
                    // shared by every modal opened this way; fixing it means plumbing
                    // onCloseAutoFocus through components/ui/dialog.tsx.
                    closePopoverAndRestoreFocus();
                    setModalOpen(true);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-xs font-black uppercase tracking-wide text-white transition-colors hover:bg-amber-700"
                >
                  <HelpCircle className="size-3.5" aria-hidden />
                  {t("itemFallback.helpCta")}
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
      <SuggestTranslationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        itemId={itemId}
        itemName={localizedName.text}
        itemImage={itemImage}
      />
    </>
  );
}
